import React, {useState, useEffect, useMemo, useRef} from "react";
import {useReactToPrint} from "react-to-print";
import {
  CirclePlay,
  ClockFading,
  Calendar,
  CircleDashed,
  Download,
  ChevronDown,
  Search,
  Monitor,
  Tag,
  CirclePercent,
  PlaySquare,
  CheckCircle,
  Clock,
  Timer,
  ArrowDownWideNarrow,
  Film,
} from "lucide-react";
import {formatTime, formatTimeMinutes} from "../../../utils/datetime";
import {getAuthHeader} from "../../../config";
import {downloadCsv} from "../../../utils/csv";
import getProgressData from "../../video/utils/getProgressData";
import getWatchedSeconds from "../../video/utils/getWatchedSeconds";
import {buildDemoUsers} from "../demoData";

// `/get-contacts` returns internal rate-limiter artifacts alongside real
// customers: records whose customer_id is prefixed `otp_throttle#` (keyed by
// phone/email) or `otp_ipthrottle#` (keyed by IP address). These are backend
// throttle counters, not user accounts, and must never appear in the admin's
// account picker (they also have no name, so one was being auto-selected as a
// blank "invalid" account). Keep only records with a real account id.
export const isRealAccount = (user) => {
  const id = user?.customer_id;
  if (!id || typeof id !== "string") return false;
  return !/^otp_(throttle|ipthrottle)#/i.test(id.trim());
};

const OrbitLoader = () => (
  <div className="flex flex-col items-center justify-center space-y-4">
    <div className="relative h-12 w-12">
      <div className="absolute inset-0 border-4 border-indigo-100 rounded-full"></div>
      <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
    </div>
  </div>
);

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  if (isNaN(date)) return "N/A";
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

const options = [
  {value: "date", label: "By date"},
  {value: "name", label: "By video name"},
  {value: "progress", label: "By progress"},
];

const StatCard = ({icon, label, sub, value, color}) => (
  <div className="bg-[#F8FAFC] p-5 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col justify-between h-full min-h-[160px] overflow-hidden">
    <div className="flex justify-between items-start mb-4 gap-3">
      <span className="font-semibold text-sm text-[#191919] flex-1 leading-tight">
        {label}
      </span>
      <div
        className={`flex-shrink-0 flex items-center justify-center ${color} w-10 h-10 rounded-2xl shadow-lg`}
      >
        {React.cloneElement(icon, {size: 20})}
      </div>
    </div>

    <div className="flex flex-col">
      <div className="text-[1.25rem] font-semibold text-[#191919] whitespace-nowrap">
        {value}
      </div>
      <div className="text-[#191919]/50 text-[11px] leading-tight">{sub}</div>
    </div>
  </div>
);

// Demo mode (see the fetch filter below): also swaps the dropdown picker for
// a full user table and trims the stat-card row for presentations.
// Read lazily (not a module const) so tests can control it per-suite.
const isDemoMode = () => Boolean(process.env.REACT_APP_DEMO_ACCOUNT_PREFIX);

// Totals for the demo user table, scoped to the selected month (startDate..
// endDate). Empty range = cumulative. Watch time and video count both reflect
// only the days inside the selected month.
const userSummary = (user, startDate, endDate) => {
  const inRange = (dk) =>
    !startDate || !endDate || (dk >= startDate && dk <= endDate);
  let watchSecs = 0;
  Object.entries(user.activity || {}).forEach(([dk, day]) => {
    if (inRange(dk)) watchSecs += Number(day?.totalVideoSeconds) || 0;
  });
  let videoCount = 0;
  let lastActive = null;
  Object.values(user.watch_history || {}).forEach((e) => {
    const days = Object.keys(e?.dailyBreakdown || {}).filter(inRange);
    if (days.length) {
      videoCount += 1;
      const latest = days.sort()[days.length - 1];
      if (!lastActive || latest > lastActive) lastActive = latest;
    }
  });
  return {watchSecs, videoCount, lastActive};
};

const AdminDashboard = () => {
  const [userData, setUserData] = useState([]);
  const [videos, setVideos] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState(1);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);

  // Pagination/View state
  const [visibleCount, setVisibleCount] = useState(5);

  const mainRef = useRef(null);
  const dropdownRef = useRef(null);
  const secondDropdownRef = useRef(null);

  const downloadPDF = useReactToPrint({
    contentRef: mainRef,
    documentTitle: `User_Analytics_${selectedUser?.customer_id || "Report"}`,
    pageStyle: `
      @page { margin: 10mm; size: A4; }
      @media print {
        html, body { height: auto !important; overflow: visible !important; }
        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background-color: white !important; }
        main { overflow: visible !important; height: auto !important; width: 100% !important; padding: 0 !important; margin: 0 !important; display: block !important; }
        .grid { display: grid !important; }
        .lg\\:grid-cols-5 { 
            display: grid !important;
            grid-template-columns: repeat(5, minmax(0, 1fr)) !important; 
            gap: 10px !important; 
            width: 100% !important;
        }
        .icon-container-print {
            width: 36px !important;
            height: 36px !important;
            min-width: 36px !important;
            max-width: 36px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
        }
        .grid-cols-12 { display: grid !important; grid-template-columns: repeat(12, minmax(0, 1fr)) !important; }
        tr, .group { page-break-inside: avoid !important; break-inside: avoid !important; }
        .shadow-sm, .shadow-2xl, .shadow-md { box-shadow: none !important; -webkit-box-shadow: none !important; }
        .view-controls-container { display: none !important; }
      }
    `,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const headers = {
          "Content-Type": "application/json",
          Authorization: await getAuthHeader(),
        };

        const [userRes, videoRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-contacts`, {
            method: "GET",
            headers,
          }),
          fetch(`${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-videos`, {
            method: "GET",
            headers,
          }),
        ]);

        const uJson = await userRes.json();
        const vJson = await videoRes.json();

        if (vJson.result === "success") setVideos(vJson.data);

        // Demo mode: REACT_APP_DEMO_ACCOUNT_PREFIX (set only on a demo machine,
        // never in CI/deploy) swaps the real account list for a fully static,
        // in-memory fake cohort with six months of distinct per-month data,
        // built from the live catalog. Nothing is fetched per-user or written
        // back. Unset = every real account, as before.
        if (isDemoMode()) {
          setUserData(
            buildDemoUsers(vJson.result === "success" ? vJson.data : []),
          );
          setSelectedUser(null);
        } else if (uJson.result === "success") {
          const demoPrefix = process.env.REACT_APP_DEMO_ACCOUNT_PREFIX;
          const rawUsers = (uJson.data || [])
            .filter(isRealAccount)
            .filter(
              (u) =>
                !demoPrefix || (u.customer_id || "").startsWith(demoPrefix),
            )
            .sort((a, b) =>
              (a.customer_firstname || "").localeCompare(
                b.customer_firstname || "",
              ),
            );
          const usersWithAvatars = await Promise.all(
            rawUsers.map(async (user) => {
              if (!user.avatar) return user;
              try {
                const avatarUrl = `${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-contact/avatar/${user.avatar}`;
                const avatarRes = await fetch(avatarUrl, {
                  method: "GET",
                  headers,
                });
                const avatarData = await avatarRes.json();
                return {...user, avatarUrl: avatarData.imageURL};
              } catch (err) {
                return user;
              }
            }),
          );
          setUserData(usersWithAvatars);
          setSelectedUser(usersWithAvatars[0]);
        }
      } catch (e) {
        console.error("Data fetching error:", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        secondDropdownRef.current &&
        !secondDropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, {passive: true});
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setVisibleCount(5);
  }, [selectedUser, sortBy, startDate, endDate]);

  const getVideoInfo = (id) => {
    const video = videos.find((v) => v.id.toString() === id.toString());
    return (
      video || {title: `Video #${id}`, category: "General", videoLength: 0}
    );
  };

  const stats = useMemo(() => {
    // Always return a populated shape (zeroed totals + empty list) so the
    // StatCards never render "undefined%" / "NaNs" while the admin is
    // switching users or when the selected user has no recorded activity.
    const totals = {secondsActive: 0, videoSecs: 0};
    const history = {};

    const isWithinRange = (dateStr) => {
      if (!startDate || !endDate) return true;
      return dateStr >= startDate && dateStr <= endDate;
    };

    if (selectedUser?.activity) {
      Object.entries(selectedUser.activity).forEach(([date, day]) => {
        if (isWithinRange(date)) {
          totals.secondsActive += Number(day?.secondsActive) || 0;
          totals.videoSecs += Number(day?.totalVideoSeconds) || 0;
        }
      });
    }

    if (selectedUser?.watch_history) {
      Object.entries(selectedUser.watch_history).forEach(([videoId, data]) => {
        let lastD = null;
        // The Watch History list is scoped to the selected month so each month
        // tab shows only the videos this user watched that month (matching the
        // month-scoped stat cards). Sum only the in-range days and drop videos
        // with no watch time in the selected range.
        const totalTime = Object.entries(data?.dailyBreakdown || {}).reduce(
          (sum, [d, v]) => {
            if (!isWithinRange(d)) return sum;
            if (!lastD || new Date(d) > new Date(lastD)) lastD = d;
            return sum + (Number(v) || 0);
          },
          0,
        );
        if (totalTime > 0)
          history[videoId] = {
            time: totalTime,
            lastDate: lastD,
            // Persisted furthest playback position. Completion is judged
            // off this (matching getProgressData used by the cards/modal),
            // NOT off accumulated watch-seconds -- a fully-watched video
            // routinely accumulates a little less than its length (throttled
            // progress ticks, buffering, partial seeks, floor rounding), so
            // the old time-based check never reached "Completed".
            lastPosition: Number(data?.lastPosition) || 0,
          };
      });
    }

    const sorted = Object.entries(history).sort((a, b) => {
      const infoA = getVideoInfo(a[0]);
      const infoB = getVideoInfo(b[0]);

      if (sortBy === "name") {
        return infoA.title.localeCompare(infoB.title);
      } else if (sortBy === "date") {
        return new Date(b[1].lastDate) - new Date(a[1].lastDate);
      } else if (sortBy === "progress") {
        const percA = a[1].lastPosition / (infoA.videoLength || 1);
        const percB = b[1].lastPosition / (infoB.videoLength || 1);
        return percB - percA;
      }
      return 0;
    });

    const completionPercentage = Math.min(
      100,
      Math.floor((totals.videoSecs / 288000) * 100),
    );
    return {totals, sorted, totalVideos: sorted.length, completionPercentage};
    // eslint-disable-next-line
  }, [selectedUser, sortBy, videos, startDate, endDate]);

  const downloadCSV = () => {
    if (!stats) return;
    const {totals, sorted} = stats;
    const rows = [
      ["Metric", "Value"],
      [
        "Customer",
        `${selectedUser.customer_firstname} ${selectedUser.customer_lastname}`,
      ],
      ["ID", selectedUser.customer_id],
      ["Date Range", `${startDate || "Start"} to ${endDate || "End"}`],
      ["Platform Time", totals.secondsActive],
      ["Hours Reported (Playtime)", totals.videoSecs],
      ["", ""],
      ["Video ID", "Video Title", "Category", "Time Watched (Seconds)"],
    ];
    sorted.forEach(([id, videoData]) => {
      const info = getVideoInfo(id);
      rows.push([id, info.title, info.category, videoData.time?.toFixed(2)]);
    });
    downloadCsv(rows, `analytics_${selectedUser.customer_id}.csv`);
  };

  const filteredUsers = userData.filter(
    (user) =>
      `${user.customer_firstname} ${user.customer_lastname}`
        .toLowerCase()
        .includes(searchQuery?.toLowerCase()?.trim()) ||
      user.customer_id
        ?.toLowerCase()
        .includes(searchQuery?.toLowerCase()?.trim()),
  );

  const setMonthRange = (offset) => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() - offset + 1, 0);
    setStartDate(firstDay.toISOString().split("T")[0]);
    setEndDate(lastDay.toISOString().split("T")[0]);
    setActiveFilter(offset);
  };

  // The current month plus the five preceding months (six total), surfaced
  // through a dropdown. Always includes the in-progress month; the demo seeds
  // all six with distinct data so each selection shows a different picture.
  const monthButtons = useMemo(() => {
    return [0, 1, 2, 3, 4, 5].map((offset) => {
      const d = new Date();
      d.setMonth(d.getMonth() - offset);
      // en-US, not "default": browser language must not change the demo UI.
      return {
        label: d.toLocaleString("en-US", {month: "long", year: "numeric"}),
        offset,
      };
    });
  }, []);

  const highlightText = (text, highlight) => {
    if (!highlight.trim()) return text;
    const regex = new RegExp(
      `(${highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi",
    );
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          className="bg-[#E2E8F0] text-gray-900 rounded-[2px] px-0"
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  // Default filter is the current month to match the month-chip order.
  useEffect(() => {
    setMonthRange(0);
  }, []);

  if (isLoading)
    return (
      <div className="py-24 flex items-center justify-center">
        <OrbitLoader />
      </div>
    );

  // Demo mode: the landing view is a table of every account, one user per
  // row, heaviest watchers first. Clicking a row opens the existing detail
  // view; "Back to all users" (below) returns here.
  if (isDemoMode() && !selectedUser) {
    const tableUsers = filteredUsers
      .map((user) => ({user, summary: userSummary(user, startDate, endDate)}))
      .sort((a, b) => b.summary.watchSecs - a.summary.watchSecs);
    return (
      <main className="flex-1 bg-white p-4 md:p-8 font-sans overflow-x-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            All users ({tableUsers.length})
          </h2>
          <div className="flex flex-col sm:flex-row items-stretch gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-52">
              <button
                onClick={() => setShowMonthDropdown((v) => !v)}
                className={`w-full bg-[#f8faff] border px-4 py-2.5 rounded-full flex items-center gap-3 transition-all duration-200 ${
                  showMonthDropdown
                    ? "border-[#3b82f6] ring-2 ring-blue-50"
                    : "border-[#e0e8ff]"
                }`}
              >
                <Calendar
                  size={18}
                  strokeWidth={2}
                  className="text-[#1a1a1a] flex-shrink-0"
                />
                <span className="flex-1 text-left text-sm font-semibold text-[#1a1a1a]">
                  {monthButtons.find((m) => m.offset === activeFilter)?.label}
                </span>
                <ChevronDown
                  size={20}
                  className={`text-[#1a1a1a] transition-transform duration-200 ${showMonthDropdown ? "rotate-180" : ""}`}
                />
              </button>
              {showMonthDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-[60]"
                    onClick={() => setShowMonthDropdown(false)}
                  ></div>
                  <div className="absolute top-[110%] right-0 left-0 sm:left-auto sm:w-56 bg-white border border-slate-100 rounded-[32px] shadow-2xl z-[70] p-4 animate-in fade-in zoom-in duration-150">
                    <p className="text-[#94a3b8] text-xs font-bold tracking-widest uppercase mb-4 ml-4">
                      Month
                    </p>
                    <div className="flex flex-col gap-1">
                      {monthButtons.map((month) => (
                        <button
                          key={month.offset}
                          onClick={() => {
                            setMonthRange(month.offset);
                            setShowMonthDropdown(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-[20px] text-sm font-medium transition-colors ${
                            activeFilter === month.offset
                              ? "bg-[#e5eefb] text-[#1a1a1a]"
                              : "bg-transparent text-[#1a1a1a] hover:bg-slate-50"
                          }`}
                        >
                          {month.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="relative w-full sm:w-72">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search name or number..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#ECECEC] rounded-full text-sm outline-none placeholder:text-gray-400 focus:border-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto border border-[#ECECEC] rounded-[20px] shadow-sm">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#ECECEC] text-xs uppercase tracking-wide text-gray-400">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Phone / email</th>
                <th className="px-4 py-3 font-semibold">Watch time</th>
                <th className="px-4 py-3 font-semibold">Videos</th>
                <th className="px-4 py-3 font-semibold">Last active</th>
              </tr>
            </thead>
            <tbody>
              {tableUsers.map(({user, summary}) => (
                <tr
                  key={user.customer_id}
                  onClick={() => setSelectedUser(user)}
                  className="border-b border-[#F3F4F6] last:border-0 cursor-pointer hover:bg-[#F8FAFC]"
                >
                  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                    {highlightText(
                      `${user.customer_firstname || ""} ${user.customer_lastname || ""}`.trim() ||
                        "(no name)",
                      searchQuery,
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {user.customer_id}
                  </td>
                  <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                    {summary.watchSecs > 0
                      ? formatTime(summary.watchSecs)
                      : "0s"}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {summary.videoCount}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {summary.lastActive
                      ? formatDate(summary.lastActive)
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    );
  }

  const {sorted} = stats;
  const visibleVideos = sorted.slice(0, visibleCount);
  const hasMore = visibleCount < sorted.length;
  const canViewLess = visibleCount > 5;

  // No min-h-screen: flows inside the app's scrolling <main>; 100vh
  // overshoots the visible mobile viewport (shell is 100dvh).
  return (
    <main
      ref={mainRef}
      className="flex-1 bg-white p-4 md:p-8 font-sans overflow-x-hidden"
    >
      {isDemoMode() && (
        <button
          onClick={() => setSelectedUser(null)}
          className="print:hidden mb-4 px-4 py-2 bg-[#F8F9FB] rounded-2xl md:rounded-xl hover:bg-gray-100 transition-colors text-sm font-semibold text-gray-900 whitespace-nowrap"
        >
          ← Back to all users
        </button>
      )}
      {/* Header Section */}
      <div className="bg-white rounded-[30px] md:rounded-[20px] p-3 md:p-4 border border-[#ECECEC] shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
        <div className="flex items-center justify-between md:justify-start gap-4 relative w-full md:w-auto">
          <div className="flex items-center gap-4">
            {selectedUser.avatarUrl ? (
              <img
                src={selectedUser.avatarUrl}
                className="w-16 h-16 md:w-10 md:h-10 rounded-full object-cover"
                alt="avatar"
              />
            ) : (
              <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-gray-300 flex items-center justify-center text-sm md:text-base font-bold text-gray-600">
                {selectedUser.customer_firstname?.charAt(0)}
                {selectedUser.customer_lastname?.charAt(0)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h2 className="text-sm md:text-base font-semibold text-gray-900 leading-tight">
                {selectedUser.customer_firstname}{" "}
                {selectedUser.customer_lastname}
              </h2>
              <p className="text-slate-400 text-xs md:text-sm">
                {selectedUser.customer_id}
              </p>
            </div>
          </div>
          <div className="relative print:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 md:p-3 bg-[#F8F9FB] rounded-2xl md:rounded-xl hover:bg-gray-100 transition-colors"
            >
              <ChevronDown
                size={20}
                className={`text-gray-900 transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isOpen && (
              <div
                className="absolute right-0 md:left-[-100px] top-16 md:top-12 w-[280px] bg-white border border-blue-100 rounded-[24px] shadow-2xl z-[100] p-4"
                ref={secondDropdownRef}
              >
                <div className="relative mb-4">
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={18}
                  />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full pl-10 pr-4 py-2.5 bg-white border-2 border-blue-500 rounded-full text-sm outline-none placeholder:text-gray-400"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="max-h-[320px] overflow-y-auto custom-scrollbar">
                  {filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => {
                        setSelectedUser(user);
                        setIsOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-2xl mb-1 ${selectedUser.customer_id === user.customer_id ? "bg-[#E8F1F8]" : "hover:bg-gray-50"}`}
                    >
                      {user.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          className="w-9 h-9 rounded-full object-cover"
                          alt=""
                        />
                      ) : (
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${user.id % 2 === 0 ? "bg-orange-50 text-orange-600" : "bg-blue-50 text-blue-600"}`}
                        >
                          {user.customer_firstname?.charAt(0) || ""}
                          {user.customer_lastname?.charAt(0) || ""}
                        </div>
                      )}
                      <div className="flex flex-col items-start leading-tight">
                        <span className="text-[15px] font-medium text-gray-800">
                          {highlightText(
                            `${user.customer_firstname || ""} ${user.customer_lastname || ""}`,
                            searchQuery,
                          )}
                        </span>
                        <p className="text-[11px] text-gray-400 font-medium">
                          {user.customer_id}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="relative print:hidden w-full md:w-auto">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="bg-zinc-900 text-white w-full md:w-auto px-6 py-2 rounded-full md:rounded-[20px] flex items-center justify-between md:justify-center gap-2 font-bold shadow-lg transition-all active:scale-95"
          >
            <div className="flex items-center gap-2 md:gap-3">
              <Download size={24} className="md:w-[18px] md:h-[18px]" />
              <span className="text-sm md:text-base font-semibold">
                Download report
              </span>
            </div>
            <ChevronDown
              size={24}
              className={`md:w-[18px] md:h-[18px] transition-transform duration-200 ${showDropdown ? "rotate-180" : ""}`}
            />
          </button>
          {showDropdown && (
            <>
              <div
                className="fixed inset-0 z-[60]"
                onClick={() => setShowDropdown(false)}
              ></div>
              <div className="absolute right-0 left-0 md:left-auto top-full mt-2 md:w-60 bg-white rounded-[2rem] shadow-2xl border border-slate-100 p-4 z-[70]">
                <div className="space-y-1">
                  <button
                    className="w-full flex items-center gap-3 p-2 rounded-[1.5rem] bg-blue-50/50 hover:bg-blue-50 transition-colors group text-left"
                    onClick={() => {
                      setShowDropdown(false);
                      downloadPDF();
                    }}
                  >
                    <div className="w-6 h-8 bg-white rounded border border-slate-200 flex flex-col items-center justify-center relative overflow-hidden shadow-sm">
                      <div className="absolute bottom-1 left-0 right-0 bg-[#E11D48] text-[8px] text-white font-bold text-center py-0.5">
                        PDF
                      </div>
                    </div>
                    <span className="text-slate-800 text-sm font-semibold truncate">
                      Download as PDF
                    </span>
                  </button>
                  <button
                    className="w-full flex items-center gap-3 p-2 rounded-[1.5rem] hover:bg-slate-50 transition-colors group text-left"
                    onClick={() => {
                      setShowDropdown(false);
                      downloadCSV();
                    }}
                  >
                    <div className="w-6 h-8 bg-white rounded border border-slate-200 flex flex-col items-center justify-center relative overflow-hidden shadow-sm">
                      <div className="absolute bottom-1 left-0 right-0 bg-[#16A34A] text-[8px] text-white font-bold text-center py-0.5">
                        CSV
                      </div>
                    </div>
                    <span className="text-slate-800 text-sm font-semibold truncate">
                      Download as CSV
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Activity Filter Section */}
      <section className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-[#262626] tracking-tight">
            User Activity
          </h3>
          <p className="text-[#262626]/50 text-sm">
            Track what this user does and time spent • Last Activity:{" "}
            <span className="text-[#262626] font-medium">
              {selectedUser.timestamp
                ? formatDate(selectedUser.timestamp)
                : "N/A"}
            </span>
          </p>
        </div>
        <div className="flex gap-4 print:hidden w-full lg:w-auto">
          <div className="relative w-full lg:w-auto lg:min-w-[210px]">
            <button
              onClick={() => setShowMonthDropdown((v) => !v)}
              className={`w-full bg-[#f8faff] border px-4 py-2.5 rounded-full flex items-center gap-3 transition-all duration-200 ${
                showMonthDropdown
                  ? "border-[#3b82f6] ring-2 ring-blue-50"
                  : "border-[#e0e8ff]"
              }`}
            >
              <Calendar
                size={18}
                strokeWidth={2}
                className="text-[#1a1a1a] flex-shrink-0"
              />
              <span className="flex-1 text-left text-sm font-semibold text-[#1a1a1a]">
                {monthButtons.find((m) => m.offset === activeFilter)?.label}
              </span>
              <ChevronDown
                size={20}
                className={`text-[#1a1a1a] transition-transform duration-200 ${showMonthDropdown ? "rotate-180" : ""}`}
              />
            </button>
            {showMonthDropdown && (
              <>
                <div
                  className="fixed inset-0 z-[60]"
                  onClick={() => setShowMonthDropdown(false)}
                ></div>
                <div className="absolute top-[110%] right-0 left-0 lg:left-auto lg:w-56 bg-white border border-slate-100 rounded-[32px] shadow-2xl z-[70] p-4 animate-in fade-in zoom-in duration-150">
                  <p className="text-[#94a3b8] text-xs font-bold tracking-widest uppercase mb-4 ml-4">
                    Month
                  </p>
                  <div className="flex flex-col gap-1">
                    {monthButtons.map((month) => (
                      <button
                        key={month.offset}
                        onClick={() => {
                          setMonthRange(month.offset);
                          setShowMonthDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-[20px] text-sm font-medium transition-colors ${
                          activeFilter === month.offset
                            ? "bg-[#e5eefb] text-[#1a1a1a]"
                            : "bg-transparent text-[#1a1a1a] hover:bg-slate-50"
                        }`}
                      >
                        {month.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="mb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 min-[800px]:grid-cols-3 min-[1100px]:grid-cols-4 min-[1280px]:grid-cols-5 gap-4">
          <StatCard
            icon={<PlaySquare className="text-white" />}
            label="Videos Watched"
            sub="Total videos"
            value={stats.totalVideos}
            color="bg-red-400"
          />
          <StatCard
            icon={<CheckCircle className="text-white" />}
            label="Hours Completed"
            sub={`${formatTimeMinutes(stats.totals.videoSecs)} of 80 hrs`}
            value={stats.completionPercentage + "%"}
            color="bg-blue-400"
          />
          <StatCard
            icon={<Clock className="text-white" />}
            label="Hours Left"
            sub="To meet requirement"
            value={(isDemoMode() ? formatTimeMinutes : formatTime)(
              Math.max(0, 288000 - Math.floor(stats.totals.videoSecs)),
            )}
            color="bg-orange-400"
          />
          {!isDemoMode() && (
            <StatCard
              icon={<Monitor className="text-white" />}
              label="Platform Time"
              sub="Time spent here"
              value={formatTime(stats.totals.secondsActive)}
              color="bg-emerald-400"
            />
          )}
          {!isDemoMode() && (
            <StatCard
              icon={<Timer className="text-white" />}
              label="Video Playtime"
              sub="Work hours completed"
              value={formatTime(stats.totals.videoSecs)}
              color="bg-purple-400"
            />
          )}
        </div>
      </section>

      {/* Watch History Title & Sort */}
      <section className="pb-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-[#262626] tracking-tight">
            Watch History
          </h3>
          <p className="text-[#262626]/50 text-sm">
            See what the users watched and for how long.
          </p>
        </div>
        <div className="print:hidden w-full lg:max-w-[240px]" ref={dropdownRef}>
          <p className="text-[#1a1a1a] text-sm mb-1 font-medium ml-1">
            Sort by:
          </p>
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`w-full bg-[#f8faff] border px-3 py-2 rounded-full flex items-center gap-3 transition-all duration-200 ${isDropdownOpen ? "border-[#3b82f6] ring-2 ring-blue-50" : "border-[#e0e8ff]"}`}
            >
              <ArrowDownWideNarrow
                size={18}
                strokeWidth={2}
                className="text-[#1a1a1a]"
              />
              <span className="flex-1 text-left text-sm font-medium text-[#1a1a1a]">
                {options.find((opt) => opt.value === sortBy)?.label}
              </span>
              <ChevronDown
                size={20}
                className={`text-[#1a1a1a] transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isDropdownOpen && (
              <div className="absolute top-[110%] left-0 w-full bg-white border border-slate-100 rounded-[32px] shadow-2xl z-[100] p-4 animate-in fade-in zoom-in duration-150">
                <div className="flex flex-col gap-1">
                  {options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-[20px] text-sm font-medium transition-colors ${sortBy === option.value ? "bg-[#e5eefb] text-[#1a1a1a]" : "bg-transparent text-[#1a1a1a] hover:bg-slate-50"}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Video Content Section */}
      <section className="pb-10">
        {/* Mobile View */}
        <div className="space-y-4 md:hidden">
          {visibleVideos.map(([id, data]) => {
            const info = getVideoInfo(id);
            const perc = Math.min(
              100,
              Math.floor((data.lastPosition / (info.videoLength || 1)) * 100),
            );
            const isCompleted =
              getProgressData(data.lastPosition, info).label === "Completed";
            return (
              <div
                key={id}
                className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-14 h-10 rounded-xl bg-slate-200 overflow-hidden shrink-0 shadow-sm">
                    <img
                      src={`https://picsum.photos/seed/${id}/100/100`}
                      className="w-full h-full object-cover"
                      alt="icon"
                    />
                  </div>
                  <h4 className="font-semibold text-[#191919] text-sm leading-snug">
                    {info.title}
                  </h4>
                </div>
                <div className="grid grid-cols-2 gap-y-4 text-[11px] font-semibold">
                  <div className="flex items-center gap-2 text-[#191919]/60">
                    <Tag size={14} /> Category
                  </div>
                  <div className="text-[#191919] font-medium text-right">
                    {info.category}
                  </div>
                  <div className="flex items-center gap-2 text-[#191919]/60">
                    <Calendar size={14} /> Date
                  </div>
                  <div className="text-[#191919] font-medium text-right">
                    {formatDate(data.lastDate)}
                  </div>
                  <div className="flex items-center gap-2 text-[#191919]/60">
                    <CirclePercent size={14} /> Progress
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500"
                        style={{width: `${perc}%`}}
                      />
                    </div>
                    <span className="text-[#191919] font-medium text-right">
                      {perc}%
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[#191919]/60">
                    <CirclePlay size={14} /> Watched
                  </div>
                  <div className="text-[#191919] font-medium text-right">
                    {formatTime(getWatchedSeconds(data.lastPosition, info))}
                  </div>
                  <div className="flex items-center gap-2 text-[#191919]/60">
                    <ClockFading size={14} /> Length
                  </div>
                  <div className="text-[#191919] font-medium text-right">
                    {formatTime(info.videoLength)}
                  </div>
                  <div className="flex items-center gap-2 text-[#191919]/60">
                    <CircleDashed size={14} /> Status
                  </div>
                  <div className="flex items-center justify-end">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] uppercase border font-black ${isCompleted ? "bg-emerald-50 text-emerald-500 border-emerald-100" : "bg-orange-50 text-orange-500 border-orange-100"}`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${isCompleted ? "bg-emerald-500" : "bg-orange-500"}`}
                      />
                      {isCompleted ? "Completed" : "In Progress"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-separate border-spacing-y-2">
            <thead>
              <tr className="text-left text-[10px] font-black text-slate-400 tracking-[0.2em]">
                <th className="pb-4 px-2">
                  <span className="flex items-center gap-1">
                    <Film size={14} /> Video Name
                  </span>
                </th>
                <th className="pb-4 px-2">
                  <span className="flex items-center gap-1">
                    <Tag size={14} /> Category
                  </span>
                </th>
                <th className="pb-4 px-2">
                  <span className="flex items-center gap-1">
                    <Calendar size={14} /> Date
                  </span>
                </th>
                <th className="pb-4 px-2 text-center">
                  <span className="flex items-center gap-1">
                    <CirclePercent size={14} /> Progress
                  </span>
                </th>
                <th className="pb-4 px-2">
                  <span className="flex items-center gap-1">
                    <CirclePlay size={14} /> Watched
                  </span>
                </th>
                <th className="pb-4 px-2">
                  <span className="flex items-center gap-1">
                    <ClockFading size={14} /> Length
                  </span>
                </th>
                <th className="pb-4 px-2">
                  <span className="flex items-center gap-1">
                    <CircleDashed size={14} /> Status
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {visibleVideos.map(([id, videoData]) => {
                const {lastDate} = videoData;
                const info = getVideoInfo(id);
                const percentage = Math.min(
                  100,
                  Math.floor(
                    (videoData.lastPosition / (info.videoLength || 1)) * 100,
                  ),
                );
                const isCompleted =
                  getProgressData(videoData.lastPosition, info).label ===
                  "Completed";
                const radius = 16;
                const circumference = 2 * Math.PI * radius;
                const offset =
                  circumference - (percentage / 100) * circumference;
                return (
                  <tr
                    key={id}
                    className="hover:bg-slate-50 transition-colors group"
                  >
                    <td className="py-4 px-2 flex items-center gap-3">
                      <div className="w-10 h-8 rounded-lg bg-slate-200 overflow-hidden shadow-sm">
                        <img
                          src={`https://picsum.photos/seed/${id}/100/100`}
                          className="w-full h-full object-cover"
                          alt="icon"
                        />
                      </div>
                      <span className="font-semibold text-xs text-[#191919]">
                        {info.title}
                      </span>
                    </td>
                    <td className="py-4 px-2 text-xs font-semibold text-[#191919]/80">
                      {info.category}
                    </td>
                    <td className="py-4 px-2 text-[10px] font-semibold text-[#191919]/50">
                      {formatDate(lastDate)}
                    </td>
                    <td className="py-4 px-2 text-center font-black text-xs text-indigo-600">
                      <div className="relative w-10 h-10 flex items-center justify-center mx-auto">
                        <svg
                          className="w-full h-full transform -rotate-90"
                          viewBox="0 0 40 40"
                        >
                          <circle
                            cx="20"
                            cy="20"
                            r={radius}
                            stroke="#f1f5f9"
                            strokeWidth="2"
                            fill="transparent"
                          />
                          <circle
                            cx="20"
                            cy="20"
                            r={radius}
                            stroke="#3B82F6"
                            strokeWidth="2"
                            fill="transparent"
                            strokeDasharray={circumference}
                            strokeDashoffset={offset}
                            strokeLinecap="round"
                          />
                        </svg>
                        <span className="absolute text-[8px] font-semibold text-[#191919]/50">
                          {percentage}%
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-2 text-[10px] font-semibold text-[#191919]/50">
                      {formatTime(
                        getWatchedSeconds(videoData.lastPosition, info),
                      )}
                    </td>
                    <td className="py-4 px-2 text-[10px] font-semibold text-[#191919]/50">
                      {formatTime(info.videoLength)}
                    </td>
                    <td className="py-4 px-2">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold ${isCompleted ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-orange-50 text-orange-600 border border-orange-100"}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full shrink-0 ${isCompleted ? "bg-emerald-500" : "bg-orange-500"}`}
                        />
                        {isCompleted ? "Completed" : "In Progress"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* View Controls Container */}
        <div className="view-controls-container mt-12 mb-8 flex flex-col gap-3 w-full print:hidden">
          {hasMore && (
            <button
              onClick={() => setVisibleCount((prev) => prev + 5)}
              className="bg-[#F8FAFC] w-full group relative flex items-center justify-center gap-2 px-8 py-4 border-2 border-slate-100 rounded-full hover:border-indigo-100 hover:bg-indigo-50/30 transition-all duration-300 shadow-sm active:scale-[0.99]"
            >
              <span className="text-sm font-bold text-slate-600 group-hover:text-indigo-600 transition-colors">
                View More
              </span>
            </button>
          )}

          {canViewLess && (
            <button
              onClick={() => setVisibleCount(5)}
              className="bg-white w-full group relative flex items-center justify-center gap-2 px-8 py-4 border-2 border-slate-100 rounded-full hover:border-red-100 hover:bg-red-50/30 transition-all duration-300 shadow-sm active:scale-[0.99]"
            >
              <span className="text-sm font-bold text-slate-600 group-hover:text-red-600 transition-colors">
                View Less
              </span>
            </button>
          )}
        </div>
      </section>
    </main>
  );
};

export default AdminDashboard;
